import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Bell, Clock, Save, Play, History, Package, Wrench, Shield,
  Ticket, AlertTriangle, Check, X, Loader2, CalendarDays, Zap
} from 'lucide-react';

const ALERT_TYPES = [
  { key: 'low_stock', label: 'Stoc scazut', desc: 'Articole sub stocul minim', icon: Package, color: 'text-amber-600' },
  { key: 'overdue_revisions', label: 'Revizii restante', desc: 'Revizii planificate depasiste', icon: Wrench, color: 'text-blue-600' },
  { key: 'overdue_preventive', label: 'Preventiva restanta', desc: 'Intrari mentenanta nefinalizate', icon: CalendarDays, color: 'text-purple-600' },
  { key: 'iscir_expiring', label: 'ISCIR expira', desc: 'Autorizatii care expira in 30 zile', icon: Shield, color: 'text-red-600' },
  { key: 'open_tickets', label: 'Tichete deschise', desc: 'Tichete active nerezolvate', icon: Ticket, color: 'text-orange-600' },
  { key: 'sla_breached', label: 'SLA depasit', desc: 'Tichete cu deadline depasit', icon: AlertTriangle, color: 'text-red-700' },
];

const DAYS = [
  { value: '0', label: 'Luni' }, { value: '1', label: 'Marti' }, { value: '2', label: 'Miercuri' },
  { value: '3', label: 'Joi' }, { value: '4', label: 'Vineri' }, { value: '5', label: 'Sambata' }, { value: '6', label: 'Duminica' },
];

const ScheduledNotificationsPage = () => {
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const [cfgRes, histRes] = await Promise.all([
        api.get('/scheduled-notifications/config'),
        api.get('/scheduled-notifications/history?limit=15')
      ]);
      setConfig(cfgRes.data);
      setHistory(histRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put('/scheduled-notifications/config', config);
      toast.success('Configurare salvata cu succes');
    } catch (e) { toast.error('Eroare la salvare'); }
    finally { setSaving(false); }
  };

  const testNow = async () => {
    setTesting(true);
    try {
      const res = await api.post('/scheduled-notifications/test-now');
      if (res.data.notification_sent) {
        toast.success(`Digest generat: ${res.data.alerts_found} alerte trimise`);
      } else {
        toast.info(res.data.message || 'Nicio alerta activa');
      }
      fetchConfig();
    } catch (e) { toast.error('Eroare la executie test'); }
    finally { setTesting(false); }
  };

  const updateAlertType = (key, enabled) => {
    setConfig(prev => ({
      ...prev,
      alert_types: { ...prev.alert_types, [key]: enabled }
    }));
  };

  const toggleRole = (role) => {
    setConfig(prev => {
      const roles = prev.target_roles || [];
      return {
        ...prev,
        target_roles: roles.includes(role) ? roles.filter(r => r !== role) : [...roles, role]
      };
    });
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
    </div>
  );

  if (!config) return null;

  const enabledCount = ALERT_TYPES.filter(t => config.alert_types?.[t.key]).length;

  return (
    <div className="max-w-4xl mx-auto p-4 lg:p-6 pb-24 space-y-6" data-testid="scheduled-notifications-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Bell className="w-6 h-6 text-blue-600" />
            Notificari Programate
          </h1>
          <p className="text-sm text-slate-500 mt-1">Rezumat zilnic automat cu alerte pe dashboard</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={testNow} disabled={testing} data-testid="test-now-btn">
            {testing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
            Testeaza Acum
          </Button>
          <Button size="sm" onClick={saveConfig} disabled={saving} data-testid="save-config-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Salveaza
          </Button>
        </div>
      </div>

      {/* Main Toggle + Schedule */}
      <Card className="border-2">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${config.enabled ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                <Zap className={`w-5 h-5 ${config.enabled ? 'text-emerald-600' : 'text-slate-400'}`} />
              </div>
              <div>
                <p className="font-bold text-slate-900">Scheduler Automat</p>
                <p className="text-xs text-slate-500">Genereaza rezumat zilnic la ora specificata</p>
              </div>
            </div>
            <Switch
              checked={config.enabled}
              onCheckedChange={(v) => setConfig(prev => ({ ...prev, enabled: v }))}
              data-testid="scheduler-toggle"
            />
          </div>

          <div className={`space-y-4 transition-opacity ${config.enabled ? 'opacity-100' : 'opacity-40'}`}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Frequency */}
              <div>
                <Label className="text-xs text-slate-500 font-semibold">Frecventa</Label>
                <Select value={config.frequency || 'daily'} onValueChange={(v) => setConfig(prev => ({ ...prev, frequency: v }))}>
                  <SelectTrigger className="h-9 mt-1" data-testid="frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Zilnic</SelectItem>
                    <SelectItem value="weekly">Saptamanal</SelectItem>
                    <SelectItem value="monthly">Lunar</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Time */}
              <div>
                <Label className="text-xs text-slate-500 font-semibold">Ora trimitere</Label>
                <div className="flex gap-1 mt-1">
                  <Select value={String(config.schedule_hour ?? 7)} onValueChange={(v) => setConfig(prev => ({ ...prev, schedule_hour: parseInt(v) }))}>
                    <SelectTrigger className="h-9 flex-1" data-testid="hour-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-48">
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>{String(i).padStart(2, '0')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="flex items-center text-lg font-bold text-slate-400">:</span>
                  <Select value={String(config.schedule_minute ?? 0)} onValueChange={(v) => setConfig(prev => ({ ...prev, schedule_minute: parseInt(v) }))}>
                    <SelectTrigger className="h-9 flex-1" data-testid="minute-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[0, 15, 30, 45].map(m => (
                        <SelectItem key={m} value={String(m)}>{String(m).padStart(2, '0')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Weekly day or Monthly day */}
              {config.frequency === 'weekly' && (
                <div>
                  <Label className="text-xs text-slate-500 font-semibold">Ziua saptamanii</Label>
                  <Select value={String(config.weekly_day ?? 0)} onValueChange={(v) => setConfig(prev => ({ ...prev, weekly_day: parseInt(v) }))}>
                    <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DAYS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {config.frequency === 'monthly' && (
                <div>
                  <Label className="text-xs text-slate-500 font-semibold">Ziua lunii</Label>
                  <Select value={String(config.monthly_day ?? 1)} onValueChange={(v) => setConfig(prev => ({ ...prev, monthly_day: parseInt(v) }))}>
                    <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-48">
                      {Array.from({ length: 28 }, (_, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alert Types */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-slate-900 text-sm">Tipuri de Alerte in Digest</h3>
              <p className="text-xs text-slate-500">{enabledCount}/{ALERT_TYPES.length} active</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ALERT_TYPES.map(at => {
              const enabled = config.alert_types?.[at.key] ?? false;
              return (
                <button
                  key={at.key}
                  onClick={() => updateAlertType(at.key, !enabled)}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                    enabled ? 'border-blue-300 bg-blue-50/50' : 'border-slate-200 bg-white opacity-60'
                  }`}
                  data-testid={`alert-type-${at.key}`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${enabled ? 'bg-white shadow-sm' : 'bg-slate-100'}`}>
                    <at.icon className={`w-4 h-4 ${enabled ? at.color : 'text-slate-400'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{at.label}</p>
                    <p className="text-[10px] text-slate-500 truncate">{at.desc}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    enabled ? 'border-blue-500 bg-blue-500' : 'border-slate-300'
                  }`}>
                    {enabled && <Check className="w-3 h-3 text-white" />}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Target Roles */}
      <Card>
        <CardContent className="p-5">
          <h3 className="font-bold text-slate-900 text-sm mb-3">Destinatari</h3>
          <p className="text-xs text-slate-500 mb-3">Selectati rolurile care primesc notificarea</p>
          <div className="flex flex-wrap gap-2">
            {['admin', 'manager', 'technician', 'procurement'].map(role => {
              const selected = (config.target_roles || []).includes(role);
              const labels = { admin: 'Administrator', manager: 'Manager', technician: 'Tehnician', procurement: 'Achizitii' };
              return (
                <button
                  key={role}
                  onClick={() => toggleRole(role)}
                  className={`px-3 py-1.5 rounded-lg border-2 text-xs font-semibold transition-all ${
                    selected ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'
                  }`}
                  data-testid={`role-${role}`}
                >
                  {labels[role]}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardContent className="p-5">
          <h3 className="font-bold text-slate-900 text-sm mb-3 flex items-center gap-2">
            <History className="w-4 h-4 text-slate-400" />
            Istoric Executii
          </h3>
          {history.length === 0 ? (
            <div className="py-6 text-center text-slate-400 text-sm">Nicio executie inregistrata</div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {history.map(h => (
                <div key={h.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`history-${h.id}`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center ${h.notification_sent ? 'bg-emerald-100' : 'bg-slate-200'}`}>
                      {h.notification_sent ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <X className="w-3.5 h-3.5 text-slate-400" />}
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-700">
                        {h.notification_sent ? `${h.alerts_found} alerte trimise` : h.reason || 'Fara alerte'}
                        {h.forced && <Badge variant="outline" className="ml-1 text-[9px] py-0">Manual</Badge>}
                      </p>
                      <p className="text-[10px] text-slate-400">
                        {new Date(h.executed_at).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        {h.target_users_count > 0 && ` | ${h.target_users_count} utilizatori`}
                      </p>
                    </div>
                  </div>
                  {h.sections && h.sections.length > 0 && (
                    <div className="text-right">
                      {h.sections.slice(0, 2).map((s, i) => (
                        <p key={i} className="text-[9px] text-slate-500 truncate max-w-[200px]">{s}</p>
                      ))}
                      {h.sections.length > 2 && <p className="text-[9px] text-slate-400">+{h.sections.length - 2} mai mult</p>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ScheduledNotificationsPage;
